/**
 * SM-2 Spaced Repetition Algorithm
 * 
 * I(1) := 1
 * I(2) := 6
 * for n > 2: I(n) := Round(I(n-1) * EF)
 * 
 * EF' := EF + (0.1 - (5 - q) * (0.08 + (5 - q) * 0.02))
 * EF' := max(1.3, EF')
 * 
 * q = quality of response (0-5)
 */

export interface SRSData {
  repetition: number;
  interval: number;
  easeFactor: number;
  nextReview: Date;
  boxIndex: number; // 1-5
}

export type ReviewRating = 0 | 1 | 2 | 3 | 4 | 5;

/**
 * Maps UI buttons to SM-2 quality ratings
 * Again -> 0
 * Hard -> 3
 * Good -> 4
 * Easy -> 5
 */
export const mapRatingToQuality = (rating: 'again' | 'hard' | 'good' | 'easy'): ReviewRating => {
  switch (rating) {
    case 'again': return 0;
    case 'hard': return 3;
    case 'good': return 4;
    case 'easy': return 5;
    default: return 3;
  }
};

/**
 * Leitner System Logic
 * Success -> Next Box (max 5)
 * Fail -> Box 1
 */
export const calculateLeitnerProgress = (
  quality: ReviewRating,
  currentBox: number = 1
): number => {
  if (quality >= 3) {
    return Math.min(5, currentBox + 1);
  } else {
    return 1;
  }
};

export const calculateNextReview = (
  quality: ReviewRating,
  previousData: SRSData = {
    repetition: 0,
    interval: 0,
    easeFactor: 2.5,
    nextReview: new Date(),
    boxIndex: 1
  }
): SRSData => {
  let { repetition, interval, easeFactor, boxIndex } = previousData;

  // Update Box Index (Leitner)
  boxIndex = calculateLeitnerProgress(quality, boxIndex);

  if (quality >= 3) {
    // Correct response (SM-2 sync)
    if (repetition === 0) {
      interval = 1;
    } else if (repetition === 1) {
      interval = 6;
    } else {
      interval = Math.round(interval * easeFactor);
    }
    repetition++;
  } else {
    // Incorrect response (SM-2 sync)
    repetition = 0;
    interval = 1;
  }

  // Calculate new Ease Factor
  easeFactor = easeFactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02));
  if (easeFactor < 1.3) easeFactor = 1.3;

  const nextReview = new Date();
  nextReview.setDate(nextReview.getDate() + interval);
  nextReview.setHours(0, 0, 0, 0); // Start of day

  return {
    repetition,
    interval,
    easeFactor,
    nextReview,
    boxIndex
  };
};
